import React, { useEffect, useState } from 'react';
import axios from 'axios';
import IntranetLayout from './IntranetLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Pencil, Trash2, X, Search, FileDown, FileText } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PROGRAMMES = ['kids', 'neo', 'ruby', 'archy', 'elite'];

const emptyForm = { nom: '', prenom: '', date_naissance: '', ecole: '', programme: 'kids', parent_nom: '', parent_contact: '', parent_email: '', date_premiere_inscription: '', tee_shirt: false };

export const IntranetEleves = () => {
  const [students, setStudents] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [search, setSearch] = useState('');
  const [filterProg, setFilterProg] = useState('all');
  const user = JSON.parse(localStorage.getItem('admin_user') || '{}');
  const isAdmin = user.role === 'admin';

  const fetchStudents = () => axios.get(`${API}/intranet/students`).then(r => setStudents(r.data)).catch(console.error);
  useEffect(() => { fetchStudents(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${API}/intranet/students/${editingId}`, form);
        toast.success('Élève modifié');
      } else {
        await axios.post(`${API}/intranet/students`, form);
        toast.success('Élève ajouté');
      }
      setShowForm(false); setEditingId(null); setForm(emptyForm); fetchStudents();
    } catch { toast.error('Erreur'); }
  };

  const handleEdit = (s) => { setForm(s); setEditingId(s.id); setShowForm(true); };
  const handleDelete = async (id) => {
    if (!isAdmin) return toast.error('Seuls les administrateurs peuvent supprimer des élèves');
    if (!window.confirm('Supprimer cet élève ?')) return;
    const token = localStorage.getItem('token');
    await axios.delete(`${API}/intranet/students/${id}`, { headers: { Authorization: `Bearer ${token}` } });
    toast.success('Supprimé'); fetchStudents();
  };

  const filtered = students.filter(s => {
    const matchSearch = `${s.nom} ${s.prenom} ${s.parent_nom}`.toLowerCase().includes(search.toLowerCase());
    const matchProg = filterProg === 'all' || s.programme === filterProg;
    return matchSearch && matchProg;
  });

  return (
    <IntranetLayout>
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h2 className="font-heading text-2xl font-extrabold text-slate-900">Élèves</h2>
            <p className="text-sm text-slate-500">{students.length} élèves enregistrés</p>
          </div>
          <div className="flex gap-2">
            <a href={`${API}/intranet/export/students-pdf`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 border border-slate-200 text-slate-600 hover:bg-slate-50 px-3 py-2 rounded-full text-sm font-semibold transition-all" data-testid="export-all-pdf">
              <FileDown size={16} /> Export PDF
            </a>
            <Button onClick={() => { setShowForm(true); setEditingId(null); setForm(emptyForm); }} className="btn-primary" data-testid="add-student-btn">
              <Plus size={16} className="mr-1" /> Ajouter un élève
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Rechercher..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 rounded-xl" data-testid="search-students" />
          </div>
          <Select value={filterProg} onValueChange={setFilterProg}>
            <SelectTrigger className="w-40 rounded-xl"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous</SelectItem>
              {PROGRAMMES.map(p => <SelectItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
            <div className="bg-white rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-heading text-lg font-extrabold">{editingId ? 'Modifier' : 'Ajouter'} un élève</h3>
                <button onClick={() => setShowForm(false)}><X size={20} /></button>
              </div>
              <form onSubmit={handleSubmit} className="space-y-3" data-testid="student-form">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs font-semibold mb-1 block">Nom *</Label><Input required value={form.nom} onChange={e => setForm({...form, nom: e.target.value})} className="rounded-lg" data-testid="input-nom" /></div>
                  <div><Label className="text-xs font-semibold mb-1 block">Prénom *</Label><Input required value={form.prenom} onChange={e => setForm({...form, prenom: e.target.value})} className="rounded-lg" data-testid="input-prenom" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs font-semibold mb-1 block">Date de naissance *</Label><Input type="date" required value={form.date_naissance} onChange={e => setForm({...form, date_naissance: e.target.value})} className="rounded-lg" data-testid="input-dob" /></div>
                  <div><Label className="text-xs font-semibold mb-1 block">École</Label><Input value={form.ecole} onChange={e => setForm({...form, ecole: e.target.value})} className="rounded-lg" /></div>
                </div>
                <div><Label className="text-xs font-semibold mb-1 block">Programme *</Label>
                  <Select value={form.programme} onValueChange={v => setForm({...form, programme: v})}>
                    <SelectTrigger className="rounded-lg" data-testid="select-programme"><SelectValue /></SelectTrigger>
                    <SelectContent>{PROGRAMMES.map(p => <SelectItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)} Robotics</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs font-semibold mb-1 block">Nom du parent *</Label><Input required value={form.parent_nom} onChange={e => setForm({...form, parent_nom: e.target.value})} className="rounded-lg" data-testid="input-parent-nom" /></div>
                  <div><Label className="text-xs font-semibold mb-1 block">Contact parent *</Label><Input required value={form.parent_contact} onChange={e => setForm({...form, parent_contact: e.target.value})} className="rounded-lg" data-testid="input-parent-contact" /></div>
                </div>
                <div><Label className="text-xs font-semibold mb-1 block">Email parent</Label><Input type="email" value={form.parent_email} onChange={e => setForm({...form, parent_email: e.target.value})} className="rounded-lg" /></div>
                <div><Label className="text-xs font-semibold mb-1 block">Date première inscription</Label><Input type="date" value={form.date_premiere_inscription} onChange={e => setForm({...form, date_premiere_inscription: e.target.value})} className="rounded-lg" /></div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="tee_shirt" checked={form.tee_shirt} onChange={e => setForm({...form, tee_shirt: e.target.checked})} className="rounded" />
                  <Label htmlFor="tee_shirt" className="text-sm">Tee-Shirt reçu</Label>
                </div>
                <Button type="submit" className="w-full btn-primary justify-center" data-testid="submit-student">{editingId ? 'Enregistrer' : 'Ajouter'}</Button>
              </form>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 font-semibold text-slate-600">Élève</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600">Programme</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600 hidden md:table-cell">École</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600 hidden lg:table-cell">Parent</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600 hidden lg:table-cell">Tee-Shirt</th>
                <th className="text-right px-4 py-3 font-semibold text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-10 text-slate-400">Aucun élève trouvé</td></tr>
              ) : filtered.map(s => (
                <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`student-row-${s.id}`}>
                  <td className="px-4 py-3">
                    <p className="font-semibold text-slate-900">{s.prenom} {s.nom}</p>
                    <p className="text-xs text-slate-500">{s.date_naissance}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className="bg-blue-50 text-[#1B6FE3] text-xs font-bold px-2 py-1 rounded-full capitalize">{s.programme}</span>
                  </td>
                  <td className="px-4 py-3 text-slate-600 hidden md:table-cell">{s.ecole || '-'}</td>
                  <td className="px-4 py-3 hidden lg:table-cell">
                    <p className="text-slate-700">{s.parent_nom}</p>
                    <p className="text-xs text-slate-500">{s.parent_contact}</p>
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell">{s.tee_shirt ? <span className="text-emerald-600 font-bold">Oui</span> : <span className="text-slate-400">Non</span>}</td>
                  <td className="px-4 py-3 text-right">
                    <a href={`${API}/intranet/export/student-pdf/${s.id}`} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-[#F97316] hover:bg-orange-50 p-1.5 rounded-lg inline-block mr-1" title="Fiche PDF"><FileText size={15} /></a>
                    <button onClick={() => handleEdit(s)} className="text-[#1B6FE3] hover:bg-blue-50 p-1.5 rounded-lg mr-1"><Pencil size={15} /></button>
                    {isAdmin && <button onClick={() => handleDelete(s.id)} className="text-red-500 hover:bg-red-50 p-1.5 rounded-lg"><Trash2 size={15} /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </IntranetLayout>
  );
};

export default IntranetEleves;
